// src/components/DiscoveryPage.js

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { useJobs } from '../context/jobContext';
import styles from './DiscoveryPage.module.css';
import { toast } from 'react-toastify';
import ConfirmationModal from '../utils/ConfirmationModal';
import ContentFocusDashboardModal from './ContentFocusDashboardModal';
import HealthDashboardModal from './HealthDashboardModal';
import MarketFitDashboardModal from './MarketFitDashboardModal';
import ContentQualityDashboardModal from './ContentQualityDashboardModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router-dom';
import {
  faYoutube,
  faInstagram,
  faTiktok,
  faFacebook,
  faTwitter,
  faBlogger,
  faTelegram,
  faLinkedin,
  faVk,
} from '@fortawesome/free-brands-svg-icons';
import {
  faMagnifyingGlass,
  faPlus,
  faBoxArchive,
  faBan,
  faSync,
  faStopCircle,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { countryList } from '../utils/CountryList'; // Placeholder: see notes
import SortableTable from '../utils/SortableTable';

// Helper to get icon for matched platform
const getPlatformIcon = (platform) => {
  switch (platform) {
    case 'youtube':
      return faYoutube;
    case 'instagram':
      return faInstagram;
    case 'tiktok':
      return faTiktok;
    case 'facebook':
      return faFacebook;
    case 'twitter':
      return faTwitter;
    case 'telegram':
      return faTelegram;
    case 'linkedin':
      return faLinkedin;
    case 'vk':
      return faVk;
    default:
      return faBlogger;
  }
};

const getTierClassName = (tier) => {
  switch (tier) {
    // Health & Focus Tiers
    case 'Prime':
    case 'Great':
    case 'Excellent':
    case 'Excellent Fit':
      return styles.tierPrime;

    case 'Promising':
    case 'Good':
    case 'Good Fit':
      return styles.tierPromising;

    case 'Acceptable':
    case 'Moderate':
    case 'Moderate Fit':
      return styles.tierAcceptable;

    case 'Mid-Risk':
    case 'Low':
    case 'Low Fit':
      return styles.tierMidRisk;

    case 'High-Risk':
    case 'Poor':
    case 'Poor Fit':
      return styles.tierHighRisk;

    case 'Incomplete':
    case 'Limited Data':
      return styles.tierInsufficient;

    case 'Competitor':
      return styles.tierInsufficient;

    case 'Inactive':
      return styles.tierInactive;

    case 'Error':
      return styles.tierError;

    default:
      return styles.tierUnknown;
  }
};

const DiscoveryPage = () => {
  const {
    importInfluencers,
    moveInfluencersToHold,
    permanentlyDiscardInfluencers,
    batchRefreshOnHoldInfluencers,
    analyzeOnHoldInfluencer,
    isContentFocusModalOpen,
    contentFocusDashboardData,
    isContentFocusDashboardLoading,
    setIsContentFocusModalOpen,
    setContentFocusDashboardData,
    setIsContentFocusDashboardLoading,
    discoverySorting, // <--- Pull global sorting state
    setDiscoverySorting, // <--- Pull global dispatcher
  } = useData();

  const {
    startDiscoverySearchJob, // --- NEW ---
    discoveryJob, // <-- NEW: Get the entire job state object from context
    pollingError,
    clearDiscoveryJobError,
    setDiscoveryJobState, // <-- NEW: Get the dispatcher from context
    updateJobResultsAfterImport,
    updateJobResultsAfterMoveToHold,
    updateJobResultsAfterDiscard,
    cancelDiscoveryJob,
  } = useJobs();

  const [activePlatform, setActivePlatform] = useState('youtube');
  const [activeTab, setActiveTab] = useState('new');
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    minSubscribers: '',
    maxSubscribers: '',
    country: null,
  });
  const [selectedRows, setSelectedRows] = useState({});

  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isBatchRefreshing, setIsBatchRefreshing] = useState(false); // For "Refresh All" button
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null, // 'import' or 'discard'
    data: [],
    title: '',
    message: '',
  });

  // --- ADD NEW STATE FOR THE DASHBOARD MODAL ---
  const [isDashboardModalOpen, setIsDashboardModalOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState({}); // For individual row spinners

  // States for the new Phase 2 Modals (Market Fit & Content Quality)
  const [isMarketFitModalOpen, setIsMarketFitModalOpen] = useState(false);
  const [marketFitData, setMarketFitData] = useState(null);
  const [isContentQualityModalOpen, setIsContentQualityModalOpen] =
    useState(false);
  const [contentQualityData, setContentQualityData] = useState(null);

  // Route Interception for Job Modal Navigation
  const location = useLocation();
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // Optional: If highlightPlatformId is provided, you can automatically check its row here
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (selectedOption) => {
    setSearchParams((prev) => ({ ...prev, country: selectedOption }));
  };

  // --- REFACTORED handleSubmit to use context ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Use the dispatcher to update the global state
    setDiscoveryJobState({
      jobStatus: 'starting',
      results: null,
      error: null,
      message: 'Initializing discovery...',
    });
    setSelectedRows({});

    const criteria = {
      ...searchParams,
      country: searchParams.country ? searchParams.country.value : undefined,
    };

    const result = await startDiscoverySearchJob(criteria);

    if (result.status && result.data?.jobId) {
      setDiscoveryJobState({
        jobId: result.data.jobId,
        jobStatus: 'pending',
        jobStartTime: Date.now(),
      });
      toast.info('Search started! You can navigate away from this page.');
    } else {
      setDiscoveryJobState({
        jobStatus: 'failed',
        error: result?.message || 'Could not start the search.',
      });
      toast.error(result?.message || 'Could not start the search.');
    }
  };

  const handleCancelSearch = (e) => {
    e.preventDefault();
    cancelDiscoveryJob();
  };

  const handleBatchRefresh = async () => {
    setIsBatchRefreshing(true);
    const result = await batchRefreshOnHoldInfluencers();
    if (result.status) {
      toast.success(
        result.data?.message || 'On-Hold list refreshed successfully!',
      );
    } else {
      toast.error(result?.message || 'Failed to refresh On-Hold list.');
    }
    setIsBatchRefreshing(false);
  };

  const openConfirmationModal = (type) => {
    const influencers = Object.values(selectedRows);
    if (influencers.length === 0) return;
    // THE FIX - Part 2: Add a log to see what data is being passed to the modal.
    console.log(
      `Opening confirmation modal for action "${type}" with data:`,
      influencers,
    );
    const messages = {
      import_new: `Are you sure you want to import ${influencers.length} selected influencer(s)?`,
      import_on_hold: `Are you sure you want to import ${influencers.length} selected influencer(s) from the On-Hold list?`,
      hold: `Are you sure you want to move ${influencers.length} selected influencer(s) to the On-Hold list for evaluation?`,
      // Add message for the new action
      permanent_discard_new: `Are you sure you want to permanently exclude ${influencers.length} selected influencer(s)? They will not appear in future searches. This action cannot be undone.`,
      permanent_discard_on_hold: `Are you sure you want to permanently exclude ${influencers.length} selected On-Hold influencer(s)? This action cannot be undone.`,
      import_ai_reject: `You are overriding the AI. Are you sure you want to import ${influencers.length} rejected influencer(s)?`,
    };
    const titles = {
      import_new: 'Confirm Import',
      import_on_hold: 'Confirm Import',
      hold: 'Confirm Move to On-Hold',
      // Add title for the new action
      permanent_discard_new: 'Confirm Permanent Exclusion',
      permanent_discard_on_hold: 'Confirm Permanent Exclusion',
      import_ai_reject: 'Override AI & Import',
    };

    setModalState({
      isOpen: true,
      type,
      data: influencers,
      title: titles[type],
      message: messages[type],
    });
  };

  // CORRECTED: `handleConfirmAction` to call the correct API wrapper
  const handleConfirmAction = async () => {
    setIsActionLoading(true);
    const { type, data } = modalState;
    let result;

    if (type === 'import_new') {
      result = await importInfluencers('youtube', data, 'new');
    } else if (type === 'import_on_hold') {
      result = await importInfluencers('youtube', data, 'on_hold');
    } else if (type === 'hold') {
      result = await moveInfluencersToHold(data);
    } else if (type === 'import_ai_reject') {
      // Re-use the existing import function but pass the new source
      result = await importInfluencers('youtube', data, 'ai_rejected');
    } else if (
      type === 'permanent_discard_new' ||
      type === 'permanent_discard_on_hold'
    ) {
      result = await permanentlyDiscardInfluencers(data);
    }

    if (result && result.status) {
      toast.success(result.data?.message || 'Action completed successfully!');

      // --- THE FINAL, CLEAN LOGIC ---
      // Call the correct dispatcher from JobContext based on the action type.
      if (
        type === 'import_new' ||
        type === 'similar' ||
        type === 'import_ai_reject'
      ) {
        const platformIdsToRemove = data.map((inf) => inf.platformId);
        updateJobResultsAfterImport(platformIdsToRemove);
      } else if (type === 'hold') {
        updateJobResultsAfterMoveToHold(result.data.on_hold_influencers);
      } else if (
        type === 'permanent_discard_new' ||
        type === 'permanent_discard_on_hold'
      ) {
        updateJobResultsAfterDiscard(result.data.discarded_influencers);
      }

      setSelectedRows({});
    } else {
      toast.error(result?.message || 'An error occurred.');
    }

    setIsActionLoading(false);
    setModalState({ isOpen: false });
  };

  const handleSelectRow = (e, influencerObject) => {
    // The second argument `influencerObject` is now the data object itself, not the table's row object.
    // We no longer need to access `.original`.

    // Add a check to prevent crashes if the object is unexpectedly undefined.
    if (!influencerObject) {
      console.error(
        'handleSelectRow was called with an undefined influencer object.',
      );
      return;
    }

    // We get the unique ID directly from the influencer object.
    const rowId = influencerObject.platformId || influencerObject.id;

    console.log(
      `Row selection changed for ID ${rowId}. Checked: ${e.target.checked}`,
      influencerObject,
    );

    setSelectedRows((prev) => {
      const newSelected = { ...prev };
      if (e.target.checked) {
        newSelected[rowId] = influencerObject;
      } else {
        delete newSelected[rowId];
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e, data) => {
    if (e.target.checked) {
      const allRows = data.reduce(
        (acc, row) => ({ ...acc, [row.platformId || row.id]: row }),
        {},
      );
      setSelectedRows(allRows);
    } else {
      setSelectedRows({});
    }
  };
  // ---
  // This handler doesn't exist yet, let's create it.
  const handleSingleRefresh = async (influencer) => {
    setRowLoading((prev) => ({ ...prev, [influencer.id]: true }));
    // This is where we would call a single-refresh API. Since we deprecated it
    // for a batch-only approach, we have two options:
    // 1. Re-introduce the single refresh endpoint (best for UX)
    // 2. Disable this button and only allow batch refresh (simpler backend)

    // For the best UX, let's assume we re-introduce a single refresh endpoint.
    // Let's create a placeholder function for it in DataContext.
    // const result = await singleRefreshOnHoldInfluencer(influencer.id);

    // For now, let's simulate it and show a toast.
    await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate API call
    toast.info(
      `Individual refresh for "${influencer.name}" would happen here.`,
    );

    setRowLoading((prev) => ({ ...prev, [influencer.id]: false }));
  };

  // --- ADD NEW HANDLER for opening the modal ---
  const handleOpenHealthDashboard = useCallback(
    async (influencer, source) => {
      if (
        influencer.healthTier === 'Inactive' ||
        influencer.healthTier === 'Error'
      ) {
        // The 'rationale' field from the backend now contains the specific reason.
        toast.info(
          influencer.rationale ||
            `This channel's status is ${influencer.healthTier}.`,
        );
        return; // Stop execution; do not open the modal.
      }

      setIsDashboardLoading(true);
      setIsDashboardModalOpen(true);
      let result;

      if (source === 'on_hold_llm') {
        result = await analyzeOnHoldInfluencer(influencer.id);
      } else {
        // For "New" influencers, data is pre-fetched. No API call needed.
        setDashboardData(influencer);
        setIsDashboardLoading(false);
        return;
      }

      if (result && result.status) {
        setDashboardData(result.data);
      } else {
        toast.error(`Analysis failed: ${result?.message}`);
        setIsDashboardModalOpen(false); // Close modal on failure
      }
      setIsDashboardLoading(false);
    },
    [analyzeOnHoldInfluencer],
  );

  const handleOpenContentFocusDashboard = useCallback(
    (influencer) => {
      setIsContentFocusDashboardLoading(true);
      if (influencer && influencer.contentFocusAnalysis) {
        setContentFocusDashboardData(influencer.contentFocusAnalysis);
        setIsContentFocusModalOpen(true);
      } else {
        toast.warn(
          'Content focus analysis is not available for this influencer.',
        );
      }
      setIsContentFocusDashboardLoading(false);
    },
    [
      setContentFocusDashboardData,
      setIsContentFocusModalOpen,
      setIsContentFocusDashboardLoading,
    ],
  );

  const handleOpenMarketFitDashboard = useCallback((influencer) => {
    if (influencer?.marketFitAnalysis) {
      setMarketFitData(influencer.marketFitAnalysis);
      setIsMarketFitModalOpen(true);
    } else {
      toast.warn('Market Fit analysis is not available for this influencer.');
    }
  }, []);

  const handleOpenContentQualityDashboard = useCallback((influencer) => {
    if (influencer?.contentQualityAnalysis) {
      setContentQualityData(influencer.contentQualityAnalysis);
      setIsContentQualityModalOpen(true);
    } else {
      toast.warn(
        'Content Quality analysis is not available for this influencer.',
      );
    }
  }, []);

  // Define columns in the format react-table expects
  const baseColumns = useMemo(
    () => [
      {
        header: 'Channel',
        accessorKey: 'name',
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.channelCell}>
            <img
              src={row.original.thumbnailUrl}
              alt={`${row.original.name} thumbnail`}
            />
            <div>
              <strong>{row.original.name}</strong>
              <a
                href={row.original.channelUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {row.original.handle || 'View Channel'}
              </a>
            </div>
          </div>
        ),
        size: 290,
      },
      {
        header: 'Country',
        accessorKey: 'countryFullName',
        cell: (info) => info.getValue() || 'N/A',
        size: 140,
      },
      {
        header: 'Followers',
        accessorKey: 'subscriberCount',
        cell: (info) =>
          info.getValue() ? Number(info.getValue()).toLocaleString() : 'N/A',
        size: 160,
      },
      {
        header: 'Views',
        accessorKey: 'viewCount',
        cell: (info) =>
          info.getValue() ? Number(info.getValue()).toLocaleString() : 'N/A',
        size: 120,
      },
      {
        header: 'Videos',
        accessorKey: 'relevantVideoCount',
        cell: (info) => info.getValue(),
        size: 120,
      },
    ],
    [],
  );

  // Columns for the "New Influencers" tab (same as base)
  const newColumns = useMemo(
    () => [
      ...baseColumns.slice(0, 3),
      {
        header: 'Focus Tier',
        accessorKey: 'contentFocusAnalysis.focusTier',
        cell: ({ row }) => {
          const analysis = row.original.contentFocusAnalysis;
          const tier = analysis?.focusTier || 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)}`}
              onClick={() => handleOpenContentFocusDashboard(row.original)}
              title="Click to see Content Focus Analysis"
            >
              {tier}
            </div>
          );
        },
        size: 160,
      },
      {
        header: 'Health Tier',
        accessorKey: 'healthTier',
        cell: ({ row }) => {
          const tier = row.original.healthTier || 'N/A';
          const rationale =
            row.original.rationale || `Click to see Channel Health Analysis`;

          return (
            <div
              className={`${styles.healthTierCell} ${getTierClassName(tier)}`}
              onClick={() => handleOpenHealthDashboard(row.original, 'new')}
              title={rationale} // <-- NEW: Add informative tooltip
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      ...baseColumns.slice(3),
    ],
    [baseColumns, handleOpenHealthDashboard, handleOpenContentFocusDashboard],
  );

  const onHoldColumns = useMemo(
    () => [
      {
        header: 'Channel',
        accessorKey: 'name',
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.channelCell}>
            <img
              src={row.original.thumbnailUrl}
              alt={`${row.original.name} thumbnail`}
            />
            <div>
              <strong>{row.original.name}</strong>
              <a
                href={row.original.channelUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {row.original.handle || 'View Channel'}
              </a>
            </div>
          </div>
        ),
        size: 260,
      },
      {
        header: 'Country',
        accessorKey: 'countryFullName',
        cell: (info) => info.getValue() || 'N/A',
        size: 150,
      },
      {
        header: 'Followers',
        accessorKey: 'subscriberCount',
        cell: (info) =>
          info.getValue() ? Number(info.getValue()).toLocaleString() : 'N/A',
        size: 160,
      },
      {
        header: 'Focus Tier',
        accessorKey: 'contentFocusAnalysis.focusTier',
        cell: ({ row }) => {
          const tier = row.original.contentFocusAnalysis?.focusTier;
          return (
            <span
              className={`${styles.marketFitTierCell} ${styles.nonClickable}`}
            >
              {tier || 'N/A'}
            </span>
          );
        },
        size: 160,
      },
      {
        header: 'Health Tier',
        accessorKey: 'healthTier',
        cell: ({ row }) => {
          const influencer = row.original;
          const tier = influencer.healthTier;
          const isClickable = tier === 'Promising' || tier === 'Prime';
          return (
            <div
              className={`${styles.healthTierCell} ${getTierClassName(tier)} ${isClickable ? '' : styles.nonClickable}`}
              onClick={() =>
                isClickable &&
                handleOpenHealthDashboard(influencer, 'on_hold_llm')
              }
              title={
                isClickable
                  ? 'Click to run deep LLM analysis'
                  : `Tier is ${tier || 'N/A'}`
              }
            >
              {tier || 'N/A'}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Score',
        accessorKey: 'healthScore',
        cell: (info) => info.getValue()?.toFixed(2) || 'N/A',
        size: 120,
      },
      {
        header: 'Updated',
        accessorKey: 'last_health_assessment_at',
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : 'N/A',
        size: 150,
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          const influencer = row.original;
          const isLoading = rowLoading[influencer.id];
          return (
            <div className={styles.actionCell}>
              <button
                onClick={() => handleSingleRefresh(influencer)}
                disabled={isLoading}
                className={styles.actionIconBtn}
                title="Refresh this influencer's health"
              >
                {isLoading ? (
                  <div className={styles.miniSpinner}></div>
                ) : (
                  <FontAwesomeIcon icon={faSync} />
                )}
              </button>
            </div>
          );
        },
        size: 110,
        enableSorting: false,
      },
    ],
    [rowLoading, handleOpenHealthDashboard],
  ); // Add rowLoading as a dependency

  // =========================================================================

  // Columns for the "In Database" tab
  const existingColumns = useMemo(
    () => [
      ...baseColumns,
      {
        header: 'Manager',
        accessorKey: 'importedBy',
        cell: (info) => info.getValue() || 'N/A',
        size: 150,
      },
      {
        header: 'Found At',
        accessorKey: 'importedAt',
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : 'N/A',
        size: 150,
      },
    ],
    [baseColumns],
  );

  const similarColumns = useMemo(
    () => [
      ...baseColumns,
      {
        header: 'Matched Profile',
        accessorKey: 'matchedData',
        cell: ({ row }) => {
          const match = row.original.matchedData;
          if (!match) return 'N/A';
          return (
            <div className={styles.matchedCell}>
              <FontAwesomeIcon
                icon={getPlatformIcon(match.matchedPlatform)}
                title={match.matchedPlatform}
              />
              <div>
                <strong>{match.matchedName}</strong>
                <a
                  href={match.matchedLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {match.matchedHandle || 'View Profile'}
                </a>
              </div>
            </div>
          );
        },
        size: 250,
      },
      {
        header: 'Match Reason', // <-- NEW COLUMN
        accessorKey: 'matchedData.matchReason',
        cell: (info) => (
          <span className={styles.matchReasonText}>
            {info.getValue() || 'N/A'}
          </span>
        ),
        size: 300,
      },
      {
        header: 'Matched By',
        accessorKey: 'matchedData.importedBy',
        cell: (info) => info.getValue() || 'N/A',
        size: 150,
      },
    ],
    [baseColumns],
  );

  // Columns for the "Discarded" tab
  const discardedColumns = useMemo(
    () => [
      ...baseColumns,
      {
        header: 'Reason',
        accessorKey: 'discardReason',
        cell: (info) => info.getValue() || 'N/A',
        size: 280,
      },
      {
        header: 'Excluded by',
        accessorKey: 'discardedBy',
        cell: (info) => info.getValue() || 'N/A',
        size: 160,
      },
      {
        header: 'At',
        accessorKey: 'discardedAt',
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : 'N/A',
        size: 120,
      },
    ],
    [baseColumns],
  );

  // Columns for the "AI Rejects" tab
  const aiRejectedColumns = useMemo(
    () => [
      ...baseColumns.slice(0, 3), // Channel, Country, Followers
      {
        header: 'AI Rejection Reason',
        accessorKey: 'reasonCode',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'bold', color: '#ffb74d' }}>
            {row.original.reasonCode === 'AI_REJECT_PHASE_1'
              ? 'Phase 1: Health/Focus'
              : 'Phase 2: Fit/Quality'}
          </span>
        ),
        size: 250,
      },
      {
        header: 'Health.Tier',
        accessorKey: 'healthTier',
        cell: ({ row }) => {
          const tier = row.original.healthTier || 'N/A';
          return (
            <div
              className={`${styles.healthTierCell} ${getTierClassName(tier)}`}
              onClick={() => {
                // Mock a payload structure that HealthDashboardModal expects
                setDashboardData(
                  row.original.healthAnalysis || {
                    healthTier: tier,
                    rationale: 'No detailed rationale available',
                  },
                );
                setIsDashboardModalOpen(true);
              }}
              title="Click to see Health Analysis"
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Focus.Tier',
        accessorKey: 'contentFocusTier',
        cell: ({ row }) => {
          const tier = row.original.contentFocusTier || 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)}`}
              onClick={() => {
                setContentFocusDashboardData(
                  row.original.contentFocusAnalysis || {
                    focusTier: tier,
                    channel_summary: 'No detailed rationale available',
                  },
                );
                setIsContentFocusModalOpen(true);
              }}
              title="Click to see Content Focus Analysis"
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
      {
        header: 'Fit.Tier',
        accessorKey: 'marketFitTier',
        cell: ({ row }) => {
          const tier = row.original.marketFitTier || 'N/A';
          const isClickable = tier !== 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)} ${!isClickable ? styles.nonClickable : ''}`}
              onClick={() =>
                isClickable && handleOpenMarketFitDashboard(row.original)
              }
              title={isClickable ? 'Click to see Market Fit Analysis' : ''}
            >
              {tier}
            </div>
          );
        },
        size: 150,
      },
      {
        header: 'Quality.Tier',
        accessorKey: 'contentQualityTier',
        cell: ({ row }) => {
          const tier = row.original.contentQualityTier || 'N/A';
          const isClickable = tier !== 'N/A';
          return (
            <div
              className={`${styles.marketFitTierCell} ${getTierClassName(tier)} ${!isClickable ? styles.nonClickable : ''}`}
              onClick={() =>
                isClickable && handleOpenContentQualityDashboard(row.original)
              }
              title={isClickable ? 'Click to see Content Quality Analysis' : ''}
            >
              {tier}
            </div>
          );
        },
        size: 170,
      },
    ],
    [
      baseColumns,
      handleOpenMarketFitDashboard,
      handleOpenContentQualityDashboard,
    ],
  );

  //

  const platformOptions = [
    { key: 'youtube', icon: faYoutube, label: 'YouTube' },
    { key: 'instagram', icon: faInstagram, label: 'Instagram' },
    { key: 'tiktok', icon: faTiktok, label: 'TikTok' },
    { key: 'facebook', icon: faFacebook, label: 'Facebook' },
  ];

  // --- REFACTORED RENDERCONTENT FUNCTION ---
  const renderContent = () => {
    // SCENARIO 1: LOADING STATE (Job is starting or pending)
    if (
      discoveryJob.jobStatus === 'starting' ||
      discoveryJob.jobStatus === 'pending'
    ) {
      return (
        <div className={styles.loadingPanel}>
          <div className={styles.spinner}></div>
          {/* Show dynamic progress message if available, otherwise a generic one */}
          <p>
            {discoveryJob?.message ||
              'Your search is in progress... This can take over 6 minutes.'}
          </p>
          <p>
            You can navigate away from this page; we'll notify you when it's
            complete.
          </p>
          {/* --- NEW CANCEL BUTTON --- */}
          <button
            onClick={handleCancelSearch}
            className={styles.cancelSearchButton}
          >
            <FontAwesomeIcon icon={faStopCircle} /> Stop Search
          </button>
          {/* This panel appears ONLY if there's a temporary communication issue during polling */}
          {pollingError && (
            <div className={styles.pollingErrorPanel}>
              <p>⚠️ {pollingError}</p>
            </div>
          )}
        </div>
      );
    }

    // This flag determines if we have any results to show.
    // It's true for a successful finish OR a failed job that still returned partial results.
    const shouldRenderResults =
      discoveryJob.jobStatus === 'finished' ||
      (discoveryJob.jobStatus === 'failed' && discoveryJob.results);

    // SCENARIO 2: CATASTROPHIC FAILURE (Job failed with NO results at all)
    if (discoveryJob.jobStatus === 'failed' && !discoveryJob.results) {
      const errorMessage =
        typeof discoveryJob.error === 'object' && discoveryJob.error?.message
          ? discoveryJob.error.message
          : String(discoveryJob.error);
      return (
        <div className={styles.errorPanel}>
          <p>Error: {errorMessage}</p>
        </div>
      );
    }

    // SCENARIO 3 & 4: SUCCESS or PARTIAL SUCCESS (Job finished or failed but has data)
    if (shouldRenderResults) {
      // Destructure all possible data from the results payload, with safe defaults.
      const {
        newInfluencers = [],
        existingYouTubeInfluencers = [],
        similarInfluencers = [],
        discardedInfluencers = [], // This is now just manual discards
        aiRejectedInfluencers = [], // <-- Extract our new array!
        onHoldInfluencers: displayOnHold = [],
        warnings = [],
      } = discoveryJob.results;

      const selectedCount = Object.keys(selectedRows).length;

      const messagesToDisplay = [];
      // --- MODIFICATION START ---
      // Add the primary error first, if it exists, as it's the most important.
      if (discoveryJob.error) {
        const errorMessage =
          typeof discoveryJob.error === 'object' && discoveryJob.error?.message
            ? discoveryJob.error.message
            : String(discoveryJob.error);
        messagesToDisplay.push(errorMessage);
      }
      // --- MODIFICATION END ---

      // Then, add all the individual warnings, if they exist.
      if (warnings && warnings.length > 0) {
        messagesToDisplay.push(...warnings);
      }

      return (
        <div className={styles.resultsPanel}>
          {/* 
                This is the new intelligent notification panel.
                It renders if the job failed OR if it succeeded but has warnings.
              */}
          {messagesToDisplay.length > 0 && (
            <div className={styles.errorPanel}>
              <div>
                {' '}
                {/* Wrapper for the message content */}
                <h3>The search completed with the following notifications:</h3>
                {/* REFACTORED SECTION STARTS HERE */}
                <ul>
                  {messagesToDisplay.map((msg, index) => {
                    // Check if the message is a summary line.
                    // The phrase "failed for" is a reliable indicator from our backend logic.
                    const isSummary = msg.includes('failed for');

                    // Conditionally apply a CSS class for styling.
                    const itemClass = isSummary
                      ? styles.warningSummary
                      : styles.warningDetail;

                    return (
                      <li key={index} className={itemClass}>
                        {msg}
                      </li>
                    );
                  })}
                </ul>
                {/* REFACTORED SECTION ENDS HERE */}
              </div>
              <button
                onClick={clearDiscoveryJobError}
                className={styles.dismissErrorButton}
                title="Dismiss this notification"
              >
                &times;
              </button>
            </div>
          )}

          {/* The rest of the JSX for displaying results is unchanged */}
          <div className={styles.resultsSummary}>
            <h3>Discovery Results</h3>
            <p>
              Found{' '}
              <span className={styles.newCount}>{newInfluencers.length}</span>{' '}
              new,{' '}
              <span className={styles.similarCount}>
                {similarInfluencers.length}
              </span>{' '}
              similar,{' '}
              <span className={styles.onHoldCount}>{displayOnHold.length}</span>{' '}
              on-hold,{' '}
              <span className={styles.existingCount}>
                {existingYouTubeInfluencers.length}
              </span>{' '}
              existing YouTube, and{' '}
              <span className={styles.discardedCount}>
                {discardedInfluencers.length}
              </span>{' '}
              discarded potentials.
            </p>
          </div>

          <div className={styles.tabContainer}>
            <div className={styles.tab}>
              <button
                onClick={() => setActiveTab('new')}
                className={activeTab === 'new' ? styles.activeTab : ''}
              >
                New ({newInfluencers.length})
              </button>
            </div>
            <div className={styles.tab}>
              <button
                onClick={() => setActiveTab('similar')}
                className={activeTab === 'similar' ? styles.activeTab : ''}
              >
                Similar ({similarInfluencers.length})
              </button>
            </div>
            <div className={styles.tab}>
              <button
                onClick={() => setActiveTab('on_hold')}
                className={activeTab === 'on_hold' ? styles.activeTab : ''}
              >
                On-Hold ({displayOnHold.length})
              </button>
            </div>
            <div className={styles.tab}>
              <button
                onClick={() => setActiveTab('existing')}
                className={activeTab === 'existing' ? styles.activeTab : ''}
              >
                In Database ({existingYouTubeInfluencers.length})
              </button>
            </div>
            <div className={styles.tab}>
              <button
                onClick={() => setActiveTab('ai_rejected')}
                className={activeTab === 'ai_rejected' ? styles.activeTab : ''}
              >
                AI Rejects ({aiRejectedInfluencers.length})
              </button>
            </div>
            <div className={styles.tab}>
              <button
                onClick={() => setActiveTab('discarded')}
                className={activeTab === 'discarded' ? styles.activeTab : ''}
              >
                Manual Excluded ({discardedInfluencers.length})
              </button>
            </div>
          </div>

          {activeTab === 'on_hold' && (
            <div className={styles.onHoldHeader}>
              <p>
                Influencers under evaluation. Refresh to see the latest health
                scores before analyzing.
              </p>
              <button
                onClick={handleBatchRefresh}
                className={styles.refreshAllButton}
                disabled={isBatchRefreshing}
              >
                <FontAwesomeIcon icon={faSync} spin={isBatchRefreshing} />
                {isBatchRefreshing ? 'Refreshing...' : 'Refresh All'}
              </button>
            </div>
          )}

          {activeTab === 'new' && (
            <SortableTable
              data={newInfluencers}
              columns={newColumns}
              enableStickyHeader={true}
              stickyFirstColumn={true}
              isSelectable={true}
              onSelectRow={handleSelectRow}
              onSelectAll={(e) => handleSelectAll(e, newInfluencers)}
              selectedRows={selectedRows}
              sorting={discoverySorting.new}
              onSortingChange={(updater) => setDiscoverySorting('new', updater)}
            />
          )}
          {activeTab === 'similar' && (
            <SortableTable
              data={similarInfluencers}
              columns={similarColumns}
              enableStickyHeader={true}
              stickyFirstColumn={true}
              isSelectable={true}
              onSelectRow={handleSelectRow}
              onSelectAll={(e) => handleSelectAll(e, similarInfluencers)}
              selectedRows={selectedRows}
              sorting={discoverySorting.similar}
              onSortingChange={(updater) =>
                setDiscoverySorting('similar', updater)
              }
            />
          )}
          {activeTab === 'on_hold' && (
            <SortableTable
              data={displayOnHold}
              columns={onHoldColumns}
              enableStickyHeader={true}
              stickyFirstColumn={true}
              isSelectable={true}
              onSelectRow={handleSelectRow}
              onSelectAll={(e) => handleSelectAll(e, displayOnHold)}
              selectedRows={selectedRows}
              sorting={discoverySorting.on_hold}
              onSortingChange={(updater) =>
                setDiscoverySorting('on_hold', updater)
              }
            />
          )}
          {activeTab === 'existing' && (
            <SortableTable
              data={existingYouTubeInfluencers}
              columns={existingColumns}
              enableStickyHeader={true}
              stickyFirstColumn={true}
              sorting={discoverySorting.existing}
              onSortingChange={(updater) =>
                setDiscoverySorting('existing', updater)
              }
            />
          )}
          {activeTab === 'discarded' && (
            <SortableTable
              data={discardedInfluencers}
              columns={discardedColumns}
              enableStickyHeader={true}
              stickyFirstColumn={true}
              sorting={discoverySorting.discarded}
              onSortingChange={(updater) =>
                setDiscoverySorting('discarded', updater)
              }
            />
          )}
          {activeTab === 'ai_rejected' && (
            <SortableTable
              data={aiRejectedInfluencers}
              columns={aiRejectedColumns}
              enableStickyHeader={true}
              stickyFirstColumn={true}
              isSelectable={true}
              onSelectRow={handleSelectRow}
              onSelectAll={(e) => handleSelectAll(e, aiRejectedInfluencers)}
              selectedRows={selectedRows}
              sorting={discoverySorting.ai_rejected}
              onSortingChange={(updater) =>
                setDiscoverySorting('ai_rejected', updater)
              }
            />
          )}

          {selectedCount > 0 && (
            <div className={styles.bulkActionsContainer}>
              {(activeTab === 'new' || activeTab === 'similar') && (
                <>
                  <button
                    onClick={() => openConfirmationModal('import_new')}
                    className={styles.importButton}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Import {selectedCount}{' '}
                    Selected
                  </button>
                  <button
                    onClick={() => openConfirmationModal('hold')}
                    className={styles.holdButton}
                  >
                    <FontAwesomeIcon icon={faBoxArchive} /> Move {selectedCount}{' '}
                    to On-Hold
                  </button>
                  <button
                    onClick={() =>
                      openConfirmationModal('permanent_discard_new')
                    }
                    className={styles.discardButton}
                  >
                    <FontAwesomeIcon icon={faBan} /> Exclude {selectedCount}
                  </button>
                </>
              )}
              {activeTab === 'on_hold' && (
                <>
                  <button
                    onClick={() =>
                      openConfirmationModal('permanent_discard_on_hold')
                    }
                    className={styles.discardButton}
                  >
                    <FontAwesomeIcon icon={faBan} /> Exclude {selectedCount}
                  </button>
                  <button
                    onClick={() => openConfirmationModal('import_on_hold')}
                    className={styles.importButton}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Import {selectedCount}{' '}
                    Selected
                  </button>
                </>
              )}
              {activeTab === 'ai_rejected' && (
                <button
                  onClick={() => openConfirmationModal('import_ai_reject')}
                  className={styles.importButton}
                >
                  <FontAwesomeIcon icon={faPlus} /> Override AI & Import{' '}
                  {selectedCount}
                </button>
              )}
            </div>
          )}
        </div>
      );
    }

    // This is the default case for when the job is 'idle' (before a search is run).
    return null;
  };

  return (
    <div className={styles.DiscoveryPageContainer}>
      <div className={styles.platformSelector}>
        <h2>Choose a Platform to Discover</h2>
        <div className={styles.platformButtons}>
          {platformOptions.map((p) => (
            <button
              key={p.key}
              onClick={() => setActivePlatform(p.key)}
              className={activePlatform === p.key ? styles.active : ''}
            >
              <FontAwesomeIcon icon={p.icon} size="2x" />
              <span>{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      {activePlatform === 'youtube' ? (
        <form className={styles.searchPanel} onSubmit={handleSubmit}>
          <h3>YouTube Influencer Discovery</h3>
          <div className={styles.formGrid}>
            <div className={styles.inputGroup}>
              <label htmlFor="keyword">Search Keyword</label>
              <input
                type="text"
                id="keyword"
                name="keyword"
                value={searchParams.keyword}
                onChange={handleInputChange}
                placeholder="e.g., 'Photoshop tutorial'"
                required
              />
            </div>
            <div className={styles.inputGroup}>
              <label>Subscriber Count</label>
              <div className={styles.rangeInput}>
                <input
                  type="number"
                  name="minSubscribers"
                  value={searchParams.minSubscribers}
                  onChange={handleInputChange}
                  placeholder="Minimum"
                />
                <span> to </span>
                <input
                  type="number"
                  name="maxSubscribers"
                  value={searchParams.maxSubscribers}
                  onChange={handleInputChange}
                  placeholder="Maximum"
                />
              </div>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="country">Channel Country</label>
              <Select
                id="country"
                name="country"
                value={searchParams.country}
                onChange={handleCountryChange}
                options={countryList}
                isClearable
                placeholder="Any Country"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          <button
            type="submit"
            className={styles.actionButton}
            disabled={
              discoveryJob.jobStatus === 'starting' ||
              discoveryJob.jobStatus === 'pending'
            }
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            {discoveryJob.jobStatus === 'starting' ||
            discoveryJob.jobStatus === 'pending'
              ? 'Searching...'
              : 'Find Influencers'}
          </button>
        </form>
      ) : (
        <div className={styles.searchPanel}>
          <h3>
            {activePlatform.charAt(0).toUpperCase() + activePlatform.slice(1)}{' '}
            Discovery
          </h3>
          <p className={styles.placeholderText}>
            Discovery for this platform is under development. Please check back
            later.
          </p>
        </div>
      )}

      {/* This single function call now handles all possible states */}
      {renderContent()}

      {/* All modals remain unchanged */}
      <HealthDashboardModal
        isOpen={isDashboardModalOpen}
        onClose={() => setIsDashboardModalOpen(false)}
        data={dashboardData}
        isLoading={isDashboardLoading}
      />
      <ContentFocusDashboardModal
        isOpen={isContentFocusModalOpen}
        onClose={() => setIsContentFocusModalOpen(false)}
        data={contentFocusDashboardData}
        isLoading={isContentFocusDashboardLoading}
        tierClassName={getTierClassName(contentFocusDashboardData?.focusTier)}
      />
      <MarketFitDashboardModal
        isOpen={isMarketFitModalOpen}
        onClose={() => setIsMarketFitModalOpen(false)}
        data={marketFitData}
        isLoading={false}
        TierClassName={getTierClassName(marketFitData?.marketFitTier)}
      />
      <ContentQualityDashboardModal
        isOpen={isContentQualityModalOpen}
        onClose={() => setIsContentQualityModalOpen(false)}
        data={contentQualityData}
      />
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        onConfirm={handleConfirmAction}
        title={modalState.title}
        confirmText={modalState.type?.split('_')[0]}
        isConfirming={isActionLoading}
      >
        <p>{modalState?.message}</p>
      </ConfirmationModal>
    </div>
  );
};

export default DiscoveryPage;
